import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { Button } from './ui/Button';
import { Camera, CameraOff, FlipHorizontal, RefreshCw } from 'lucide-react';

interface ScannerProps {
  onScan: (code: string) => void;
  onError?: (error: string) => void;
}

export function Scanner({ onScan, onError }: ScannerProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [cameras, setCameras] = useState<{ id: string; label: string }[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [permissionDenied, setPermissionDenied] = useState(false);

  // Initialize scanner and get available cameras
  useEffect(() => {
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length) {
          setCameras(devices);
          // Prefer back camera on mobile
          const backCamera = devices.find(
            (d) =>
              d.label.toLowerCase().includes('back') ||
              d.label.toLowerCase().includes('rear') ||
              d.label.toLowerCase().includes('environment')
          );
          setSelectedCamera(backCamera?.id || devices[0].id);
        } else {
          setError('No cameras found');
        }
      })
      .catch((err) => {
        console.error('Error getting cameras:', err);
        if (err.toString().includes('NotAllowedError')) {
          setPermissionDenied(true);
          setError('Camera permission denied. Please allow camera access.');
        } else {
          setError('Failed to access camera');
        }
      });

    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    if (!selectedCamera || !containerRef.current) return;

    try {
      setError('');
      const scannerId = 'qr-scanner-region';

      // Create or get scanner instance
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode(scannerId);
      }

      const scanner = scannerRef.current;

      // Check if already scanning
      if (scanner.getState() === Html5QrcodeScannerState.SCANNING) {
        await scanner.stop();
      }

      await scanner.start(
        selectedCamera,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          // Successfully scanned
          onScan(decodedText);
        },
        () => {
          // QR code detection failure - this is normal when nothing is in view
        }
      );

      setIsScanning(true);
    } catch (err) {
      console.error('Error starting scanner:', err);
      const message = err instanceof Error ? err.message : 'Failed to start scanner';
      setError(message);
      onError?.(message);
    }
  };

  const stopScanning = async () => {
    try {
      if (scannerRef.current) {
        const state = scannerRef.current.getState();
        if (state === Html5QrcodeScannerState.SCANNING) {
          await scannerRef.current.stop();
        }
      }
      setIsScanning(false);
    } catch (err) {
      console.error('Error stopping scanner:', err);
    }
  };

  const switchCamera = () => {
    if (cameras.length <= 1) return;

    const currentIndex = cameras.findIndex((c) => c.id === selectedCamera);
    const nextIndex = (currentIndex + 1) % cameras.length;
    const nextCamera = cameras[nextIndex];

    setSelectedCamera(nextCamera.id);

    // If currently scanning, restart with new camera
    if (isScanning) {
      stopScanning().then(() => {
        setTimeout(() => {
          startScanning();
        }, 100);
      });
    }
  };

  if (permissionDenied) {
    return (
      <div className="text-center py-8">
        <CameraOff className="h-16 w-16 mx-auto mb-4 text-error" />
        <p className="text-lg font-medium mb-2">Camera Access Required</p>
        <p className="text-foreground-muted mb-4">
          Please allow camera access in your browser settings to use the scanner.
        </p>
        <Button onClick={() => window.location.reload()}>
          <RefreshCw className="h-4 w-4" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Scanner viewport */}
      <div
        ref={containerRef}
        className="relative bg-black rounded-lg overflow-hidden"
        style={{ minHeight: '300px' }}
      >
        <div id="qr-scanner-region" className="w-full" />

        {!isScanning && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface">
            <div className="text-center">
              <Camera className="h-16 w-16 mx-auto mb-4 text-foreground-muted" />
              <p className="text-foreground-muted">Camera is off</p>
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="p-3 text-sm text-error bg-error/10 rounded-lg text-center">
          {error}
        </div>
      )}

      {/* Controls */}
      <div className="flex justify-center gap-3">
        {!isScanning ? (
          <Button
            onClick={startScanning}
            disabled={!selectedCamera}
            size="lg"
          >
            <Camera className="h-5 w-5" />
            Start Scanning
          </Button>
        ) : (
          <>
            <Button onClick={stopScanning} variant="secondary" size="lg">
              <CameraOff className="h-5 w-5" />
              Stop
            </Button>
            {cameras.length > 1 && (
              <Button onClick={switchCamera} variant="secondary" size="lg">
                <FlipHorizontal className="h-5 w-5" />
                Switch Camera
              </Button>
            )}
          </>
        )}
      </div>

      {/* Camera selector (if multiple cameras) */}
      {cameras.length > 1 && !isScanning && (
        <div className="text-center">
          <select
            value={selectedCamera}
            onChange={(e) => setSelectedCamera(e.target.value)}
            className="bg-surface border border-border rounded px-3 py-2 text-sm"
          >
            {cameras.map((camera) => (
              <option key={camera.id} value={camera.id}>
                {camera.label || `Camera ${camera.id}`}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
